import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { DatePicker, Input } from '@cargomart/ui'
import { IconSpinner } from 'components/IconSpinner'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { format } from 'date-fns'
import { formatDateToMoscow } from 'helpers/dates'
import { DeliveryTypeEnum } from 'modules/deliveryInfo/types'
import { useLazyFindCheckOutScheduleQuery } from 'modules/order/api'
import { isDepartureDateDisabled } from 'modules/order/helpers/date'
import { TScheduleDay } from 'modules/terminal/types'
import IconTime from 'remixicon/icons/System/time-fill.svg?react'
import { SUPPORT_TEL_FORMATTED } from 'src/constants/links'
import {
  useFindDeliveryInfoDateMutation,
  useLazyGetDeliveryInfoDepartureCourierScheduleQuery
} from 'src/modules/deliveryInfo/api'
import { PointInfo } from 'src/store/orderAdd/types'

import { ModalSchedule } from '../ModalSchedule/ModalSchedule'
import { OrderDeliveryDate } from './OrderDeliveryDate'

type OrderCreateFormDateProps = {
  departureSelectedOption: Pick<NonNullable<PointInfo['selectedOption']>, 'longitude' | 'latitude'>
  departureTerminalId: string
  departureTerminalSchedule: TScheduleDay[] | undefined
  departureType: DeliveryTypeEnum | undefined
  destinationSelectedOption: Pick<NonNullable<PointInfo['selectedOption']>, 'longitude' | 'latitude'>
  destinationTerminalId: string
  destinationTerminalSchedule: TScheduleDay[] | undefined
  destinationType: DeliveryTypeEnum | undefined
  error: string | undefined
  onChange: (...event: any[]) => void
  setError: any
  value: string
  clearErrors?: any
  isGuest?: boolean
}

export const OrderCreateFormDate: FC<OrderCreateFormDateProps> = (props) => {
  const {
    isGuest,
    departureTerminalId,
    destinationTerminalId,
    departureType,
    destinationType,
    departureTerminalSchedule,
    destinationTerminalSchedule,
    value,
    onChange,
    error,
    setError,
    clearErrors,
    departureSelectedOption,
    destinationSelectedOption
  } = props
  const dispatch = useDispatch()
  const [
    getDeliveryInfoDepartureCourierSchedule,
    { data: courierSchedule, isFetching: isCourierScheduleFetching, isUninitialized: isCourierScheduleUninitialized }
  ] = useLazyGetDeliveryInfoDepartureCourierScheduleQuery()
  const [findCheckOutSchedule, { data: schedule, isLoading: isScheduleLoading }] = useLazyFindCheckOutScheduleQuery()

  const { openModal, closeModal } = useModal()
  const [
    findDeliveryInfoDate,
    { data: rawDeliveryInfo, error: deliveryInfoDateError, isLoading: isDeliveryInfoDateLoading }
  ] = useFindDeliveryInfoDateMutation()
  const [deliveryInfo, setDeliveryInfo] = useState(rawDeliveryInfo?.deliveryDate)

  const [warning, setWarning] = useState('')

  const handleOpenModalSchedule = () => {
    openModal(MODAL_IDS.OrderCreateTerminalFromDate)
  }

  const handleCloseModalSchedule = () => {
    closeModal(MODAL_IDS.OrderCreateTerminalFromDate)
  }

  const handleKeyPress = (e: any) => {
    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault()
    }
  }

  const conditionForRequest = value && departureTerminalId && destinationTerminalId && departureType
  const checkTerminalsForFalse =
    departureTerminalId !== '' &&
    destinationTerminalId !== '' &&
    departureTerminalId !== undefined &&
    destinationTerminalId !== undefined

  const isEqualTerminals = useMemo(
    () => Boolean(checkTerminalsForFalse && departureTerminalId === destinationTerminalId && value),
    [checkTerminalsForFalse, departureTerminalId, destinationTerminalId, value]
  )

  const isDisabledDate = useCallback(
    (date: Date) =>
      isDepartureDateDisabled({
        date,
        now: new Date(),
        departureType,
        courierSchedule,
        departureTerminalId,
        departureTerminalSchedule
      }),
    [courierSchedule, departureTerminalId, departureTerminalSchedule, departureType]
  )

  const departureTerminalDate = useMemo(() => {
    if (!value) {
      return null
    }

    const dayOfWeek = format(new Date(value), 'eee').toLowerCase()
    const departureDay = departureTerminalSchedule?.find((day) => day.dayOfWeek === dayOfWeek)

    return departureDay?.from && departureDay?.to ? `${departureDay?.from}-${departureDay?.to}` : null
  }, [departureTerminalSchedule, value])

  const destinationTerminalDate = useMemo(() => {
    if (!deliveryInfo?.destinationTerminalDate) {
      return null
    }

    const dayOfWeek = format(new Date(deliveryInfo.destinationTerminalDate), 'eee').toLowerCase()
    const departureDay = destinationTerminalSchedule?.find((day) => day.dayOfWeek === dayOfWeek)

    return departureDay?.from && departureDay?.to ? `${departureDay?.from}-${departureDay?.to}` : null
  }, [deliveryInfo, destinationTerminalSchedule])

  const courierDateMessage = useMemo(() => {
    if (departureType !== DeliveryTypeEnum.courier) {
      return null
    }

    if (isCourierScheduleFetching || isCourierScheduleUninitialized) {
      return null
    }

    if (!courierSchedule?.length) {
      return (
        <span className="relative -top-2 text-body-sm text-attention">
          График работы курьерской службы не доступен. Для согласования точной даты курьерского забора груза свяжитесь
          с&nbsp;поддержкой: {SUPPORT_TEL_FORMATTED}
        </span>
      )
    }

    if (value) {
      return (
        <span className="relative -top-2 text-body-sm text-on-surface-variant">
          До наступления этой даты с вами свяжется оператор для согласования времени забора груза
        </span>
      )
    }

    return null
  }, [courierSchedule, departureType, isCourierScheduleFetching, isCourierScheduleUninitialized, value])

  useEffect(() => {
    setDeliveryInfo(rawDeliveryInfo?.deliveryDate)
  }, [rawDeliveryInfo?.deliveryDate])

  useEffect(() => {
    setWarning('')
    setDeliveryInfo(undefined)
  }, [departureTerminalId, destinationTerminalId, dispatch])

  useEffect(() => {
    if (deliveryInfoDateError) {
      // @ts-ignore
      deliveryInfoDateError?.data?.message
        .filter((el: any) => el.path.includes('departureDate'))
        // eslint-disable-next-line array-callback-return
        .map((el: any) => {
          setWarning(el.title)
        })
      setDeliveryInfo(undefined)
    } else {
      setWarning('')
    }
  }, [deliveryInfoDateError]) // eslint-disable-line

  useEffect(() => {
    if (
      departureType === DeliveryTypeEnum.courier &&
      departureSelectedOption.latitude &&
      departureSelectedOption.longitude
    ) {
      getDeliveryInfoDepartureCourierSchedule({
        latitude: departureSelectedOption.latitude,
        longitude: departureSelectedOption.longitude
      })
    }
  }, [
    departureSelectedOption.latitude,
    departureSelectedOption.longitude,
    departureType,
    getDeliveryInfoDepartureCourierSchedule
  ])

  useEffect(() => {
    if (conditionForRequest && !isEqualTerminals) {
      findDeliveryInfoDate({
        departureDate: value,
        departureTerminalId,
        destinationTerminalId,
        type: departureType,
        destinationLatitude: destinationSelectedOption?.latitude,
        destinationLongitude: destinationSelectedOption?.longitude,
        destinationType
      })
    }
  }, [value, departureTerminalId, destinationTerminalId, departureType, destinationType]) // eslint-disable-line

  useEffect(() => {
    if (departureTerminalId && destinationTerminalId) {
      findCheckOutSchedule({ departureTerminalId, destinationTerminalId })
    }
  }, [value, departureTerminalId, destinationTerminalId, departureType]) // eslint-disable-line

  useEffect(() => {
    if (!value.length) {
      setDeliveryInfo(undefined)
      setWarning('')
    }
    if (!departureTerminalId || !destinationTerminalId) {
      setDeliveryInfo(undefined)
    }
  }, [value, departureTerminalId, destinationTerminalId, departureType, destinationType]) // eslint-disable-line

  useEffect(() => {
    if (value && isDisabledDate(new Date(value))) {
      onChange('')
      if (isGuest) {
        setError(true)
      } else {
        setError('departureDate', { type: 'departureDate', message: 'В указанную дату забор груза невозможен' })
      }
    }
  }, [departureType, isDisabledDate, isGuest, onChange, setError, value])

  const isSameTerminals = useMemo(
    () => destinationTerminalId && departureTerminalId && destinationTerminalId === departureTerminalId,
    [departureTerminalId, destinationTerminalId]
  )

  return (
    <>
      <div className="pt-4">
        <h2 className="pb-2 pt-1 text-headline-sm text-on-surface md:pt-4">Сроки</h2>
        <div className="text-body-nm py-2 text-on-surface">
          Отправка производится на следующий рабочий день после приёма (забора) груза.
          <a className="text-body-nm link cursor-pointer" href="#" onClick={handleOpenModalSchedule}>
            <p>График отправления</p>
          </a>
        </div>
        <div className="flex flex-col md:flex-row md:gap-2">
          <div className="flex w-full flex-col gap-1 py-2 md:w-1/2">
            <div className="text-body-nm pt-2 text-on-surface-variant">Отправление груза</div>
            <DatePicker
              disabledDays={isDisabledDate}
              invalid={Boolean(error)}
              name="departureDate"
              onKeyPress={handleKeyPress}
              value={value}
              onPick={(date) => {
                onChange(date)
                if (isGuest) {
                  clearErrors('')
                } else {
                  clearErrors('departureDate')
                }
              }}
            />
            {error ? <span className="text-body-sm text-negative">{error}</span> : ''}
          </div>
          {departureType === DeliveryTypeEnum.terminal && (
            <div className="flex w-full flex-col gap-1 py-2 md:w-1/2">
              <div className="text-body-nm pt-2 text-on-surface-variant">Рабочее время терминала</div>
              <Input
                disabled
                className="border-dashed"
                name="destinationDate"
                value={departureTerminalId && departureTerminalDate ? departureTerminalDate : '-'}
                prefix={
                  isDeliveryInfoDateLoading ? <IconSpinner size="nm" /> : <IconTime containerSize="nm" size="xs" />
                }
              />
              {deliveryInfoDateError && <span className="text-body-sm text-attention">{warning}</span>}
            </div>
          )}
        </div>
        {courierDateMessage}
      </div>
      {(deliveryInfo || isSameTerminals) && (
        <OrderDeliveryDate
          courierDate={deliveryInfo?.destinationCourierDate}
          isCourier={destinationType === DeliveryTypeEnum.courier}
          terminalWorkTime={destinationTerminalDate || undefined}
          terminalDate={
            isSameTerminals ? 'до 2 рабочих дней' : formatDateToMoscow(deliveryInfo?.destinationTerminalDate || '')
          }
        />
      )}
      <ModalItem id={MODAL_IDS.OrderCreateTerminalFromDate}>
        <ModalSchedule
          handleCloseModalSchedule={handleCloseModalSchedule}
          id="modalSchedule"
          isLoading={isScheduleLoading}
          schedule={departureTerminalId && destinationTerminalId ? schedule : []}
        />
      </ModalItem>
    </>
  )
}
